import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, RefreshCw, FileText, AlertCircle, Info, AlertTriangle, XCircle, CheckCircle, Search } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './LogsModal.css';

function LogsModal({ isOpen, onClose, user, selectedWorkspace }) {
  const { organization } = useOrganization();
  const clerk = useClerk();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(24);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [rawLogData, setRawLogData] = useState(null);
  const [loadingRawLog, setLoadingRawLog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [rawLogSearchQuery, setRawLogSearchQuery] = useState('');


  const fetchLogs = useCallback(async () => {
    if (!clerk?.session || !organization) {
      setError('Authentication required');
      return;
    }

    if (!user) {
      setError('User ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setError('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const params = new URLSearchParams({
        user_id: user, // user is already the user ID string
        workspace_id: selectedWorkspace.workspace_id,
        hours: timeRange.toString(),
        limit: '100'
      });

      if (eventTypeFilter) {
        params.append('event_type', eventTypeFilter);
      }

      const response = await fetch(`${apiBaseUrl}/logs/user-workspace?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.statusText}`);
      }

      const data = await response.json();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clerk, organization, user, selectedWorkspace, timeRange, eventTypeFilter]);

  // Fetch logs only when modal opens (not on filter changes)
  useEffect(() => {
    if (isOpen && user && selectedWorkspace && clerk?.session) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user, selectedWorkspace, clerk]);

  const handleRefresh = () => {
    fetchLogs();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      // Today: show only time
      return date.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } else {
      // Other days: show date and time
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getLevelIcon = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR':
      case 'CRITICAL':
        return <XCircle size={12} className="log-level-icon error" />;
      case 'WARN':
        return <AlertTriangle size={12} className="log-level-icon warn" />;
      case 'INFO':
        return <Info size={12} className="log-level-icon info" />;
      case 'DEBUG':
        return <FileText size={12} className="log-level-icon debug" />;
      default:
        return <CheckCircle size={12} className="log-level-icon" />;
    }
  };

  const getLevelClass = (level) => {
    return `log-level-${level?.toLowerCase() || 'info'}`;
  };

  // Get subtle background class for event type
  const getEventTypeClass = (eventType) => {
    switch (eventType) {
      case 'error':
        return 'event-type-error';
      case 'user_message':
        return 'event-type-user';
      case 'agent_response':
      case 'claude_response':
        return 'event-type-agent';
      case 'tool_call':
        return 'event-type-tool';
      default:
        return 'event-type-default';
    }
  };

  const handleLogClick = async (log) => {
    if (!log.PK || !log.SK) {
      console.error('Log entry missing PK or SK');
      return;
    }

    setSelectedLog(log);
    setLoadingRawLog(true);
    setRawLogData(null);

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setError('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(
        `${apiBaseUrl}/logs/entry?pk=${encodeURIComponent(log.PK)}&sk=${encodeURIComponent(log.SK)}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch log entry');
      }

      const data = await response.json();
      setRawLogData(data.log_entry);
    } catch (error) {
      console.error('Error fetching raw log:', error);
      setRawLogData({ error: 'Failed to load raw log data' });
    } finally {
      setLoadingRawLog(false);
    }
  };

  const closeRawLogViewer = () => {
    setSelectedLog(null);
    setRawLogData(null);
    setRawLogSearchQuery('');
  };

  // Deep search function to search through all JSON fields
  const searchInObject = (obj, query) => {
    if (!query || query.trim() === '') return true;
    
    const searchTerm = query.toLowerCase().trim();
    
    const searchRecursive = (value) => {
      if (value === null || value === undefined) return false;
      
      // If it's a string, check if it contains the search term
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchTerm);
      }
      
      // If it's a number, check if it matches
      if (typeof value === 'number') {
        return value.toString().includes(searchTerm);
      }
      
      // If it's an array, search recursively in each element
      if (Array.isArray(value)) {
        return value.some(item => searchRecursive(item));
      }
      
      // If it's an object, search recursively in all values
      if (typeof value === 'object') {
        return Object.values(value).some(val => searchRecursive(val));
      }
      
      return false;
    };
    
    return searchRecursive(obj);
  };

  // Filter logs based on search query
  const filteredLogs = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return logs;
    }
    
    return logs.filter(log => searchInObject(log, searchQuery));
  }, [logs, searchQuery]);

  const extractLogDetails = (log) => {
    // Priority 1: Error messages
    if (log.error_context?.error_message) {
      return { type: 'error', text: log.error_context.error_message };
    }

    // Priority 2: User messages or agent responses
    if (log.custom_data?.message) {
      return { type: 'message', text: log.custom_data.message };
    }
    if (log.custom_data?.response) {
      return { type: 'message', text: log.custom_data.response };
    }

    // Priority 3: Parsed content_summary (NEW - hybrid approach)
    if (log.content_summary?.display_summary) {
      const contentType = log.content_summary.content_type || 'info';
      return {
        type: contentType === 'tool_use' ? 'tool' : contentType,
        text: log.content_summary.display_summary,
        toolName: log.content_summary.tool_name
      };
    }

    // Priority 4: Fallback - parse claude_message_raw (for old logs without content_summary)
    if (log.claude_message_raw) {
      const raw = log.claude_message_raw;
      
      // Check for ToolUseBlock
      const toolMatch = raw.match(/ToolUseBlock\(id='([^']+)',\s*name='([^']+)'/);
      if (toolMatch) {
        const toolName = toolMatch[2];
        
        // Try to get description
        const descMatch = raw.match(/'description':\s*'([^']+)'/);
        if (descMatch) {
          return { type: 'tool', text: descMatch[1], toolName };
        }
        
        // Try to get command
        const cmdMatch = raw.match(/'command':\s*'([^']+)'/);
        if (cmdMatch) {
          return { type: 'tool', text: cmdMatch[1], toolName };
        }
        
        // Just show tool name
        return { type: 'tool', text: `${toolName}`, toolName };
      }

      // Check for TextBlock with content
      const textMatch = raw.match(/TextBlock\(text='([^']+)'/);
      if (textMatch) {
        return { type: 'message', text: textMatch[1] };
      }
    }

    // Priority 5: Session events
    if (log.event_type === 'session_start') {
      return { type: 'session', text: 'Session started' };
    }
    if (log.event_type === 'session_end') {
      const duration = log.custom_data?.session_duration_seconds;
      return { type: 'session', text: duration ? `Session ended (${duration.toFixed(1)}s)` : 'Session ended' };
    }

    // No meaningful data - return null
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="logs-modal-overlay" onClick={onClose}>
      <div className="logs-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="logs-modal-header">
          <div className="logs-modal-title">
            <FileText size={16} />
            <span>Activity Logs</span>
          </div>
          <button className="logs-modal-close" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* Filters and Stats */}
        <div className="logs-modal-controls">
          <div className="logs-filters">
            <div className="logs-filter-group">
              <label htmlFor="timeRange">Time Range:</label>
              <select
                id="timeRange"
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                className="logs-filter-select"
              >
                <option value={1}>Last Hour</option>
                <option value={6}>Last 6 Hours</option>
                <option value={24}>Last 24 Hours</option>
                <option value={72}>Last 3 Days</option>
                <option value={168}>Last Week</option>
                <option value={720}>Last 30 Days</option>
              </select>
            </div>

            <div className="logs-filter-group">
              <label htmlFor="eventType">Event Type:</label>
              <select
                id="eventType"
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                className="logs-filter-select"
              >
                <option value="">All Events</option>
                <option value="user_message">User Messages</option>
                <option value="agent_response">Agent Responses</option>
                <option value="tool_call">Tool Calls</option>
                <option value="error">Errors</option>
              </select>
            </div>

            <button
              className="logs-refresh-button"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh logs"
            >
              <RefreshCw size={12} className={loading ? 'spinning' : ''} />
              <span>Refresh</span>
            </button>
          </div>

          {/* Search Input */}
          <div className="logs-search-container">
            <div className="logs-search-wrapper">
              <Search size={14} className="logs-search-icon" />
              <input
                type="text"
                className="logs-search-input"
                placeholder="Search in logs (searches all JSON fields)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="logs-search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="logs-search-results">
                Found {filteredLogs.length} of {logs.length} logs
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="logs-modal-content">
          {loading ? (
            <div className="logs-loading">
              <RefreshCw size={24} className="spinning" />
              <span>Loading logs...</span>
            </div>
          ) : error ? (
            <div className="logs-error">
              <AlertCircle size={24} />
              <span>Error loading logs: {error}</span>
              <button onClick={handleRefresh} className="logs-retry-button">
                Try Again
              </button>
            </div>
          ) : logs.length === 0 ? (
            <div className="logs-empty">
              <FileText size={48} />
              <span>No logs found for this time range</span>
              <p>Try adjusting the filters or time range</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="logs-empty">
              <Search size={48} />
              <span>No logs match your search query</span>
              <p>Try a different search term or clear the search</p>
              <button onClick={() => setSearchQuery('')} className="logs-retry-button">
                Clear Search
              </button>
            </div>
          ) : (
            <div className="logs-table">
              {/* Table Header */}
              <div className="logs-table-header">
                <div className="logs-col-icon"></div>
                <div className="logs-col-timestamp">Time</div>
                <div className="logs-col-event-type">Event Type</div>
                <div className="logs-col-details">Details</div>
                <div className="logs-col-session">Session</div>
              </div>
              
              {/* Table Rows */}
              <div className="logs-table-body">
                {filteredLogs.map((log, index) => (
                  <div 
                    key={index} 
                    className={`logs-table-row logs-table-row-clickable ${getLevelClass(log.level)}`}
                    onClick={() => handleLogClick(log)}
                    title="Click to view raw JSON"
                  >
                    <div className="logs-col-icon">
                      {getLevelIcon(log.level)}
                    </div>
                    
                    <div className="logs-col-timestamp">
                      {formatTimestamp(log.timestamp)}
                    </div>
                    
                    <div className="logs-col-event-type">
                      <span className={`log-event-badge ${getEventTypeClass(log.event_type)}`}>
                        {log.event_type.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="logs-col-details">
                      {(() => {
                        const details = extractLogDetails(log);
                        
                        if (!details) {
                          return <div className="log-detail-empty">-</div>;
                        }
                        
                        const displayText = details.text.length > 150 
                          ? details.text.substring(0, 150) + '...' 
                          : details.text;
                        
                        return (
                          <>
                            <div className={`log-detail-${details.type}`}>
                              {details.type === 'error' && <strong>Error: </strong>}
                              {details.type === 'tool' && <strong>{details.toolName}: </strong>}
                              {displayText}
                            </div>
                            
                            {/* Performance metrics as secondary info */}
                            {log.performance_metrics?.response_time > 0 && (
                              <div className="log-detail-metrics">
                                {log.performance_metrics.response_time.toFixed(2)}s
                                {log.performance_metrics.memory_usage_mb > 0 && 
                                  ` • ${log.performance_metrics.memory_usage_mb.toFixed(1)}MB`}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    
                    <div className="logs-col-session" title={log.session_id}>
                      {log.session_id ? log.session_id.substring(0, 8) : '-'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="logs-modal-footer">
          <div className="logs-footer-info">
            <span>
              Showing {filteredLogs.length} {searchQuery ? 'filtered' : ''} of {logs.length} logs
            </span>
            {selectedWorkspace && (
              <span className="logs-footer-template">
                Workspace: {selectedWorkspace.workspace_name}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Raw Log JSON Viewer Modal */}
      {selectedLog && (
        <div className="raw-log-viewer-overlay" onClick={closeRawLogViewer}>
          <div className="raw-log-viewer-container" onClick={(e) => e.stopPropagation()}>
            <div className="raw-log-viewer-header">
              <h3>Raw Log Entry JSON</h3>
              <button onClick={closeRawLogViewer} className="raw-log-viewer-close">×</button>
            </div>
            {/* Search in Raw JSON */}
            {rawLogData && (
              <div className="raw-log-search-container">
                <div className="raw-log-search-wrapper">
                  <Search size={14} className="raw-log-search-icon" />
                  <input
                    type="text"
                    className="raw-log-search-input"
                    placeholder="Search in JSON..."
                    value={rawLogSearchQuery}
                    onChange={(e) => setRawLogSearchQuery(e.target.value)}
                  />
                  {rawLogSearchQuery && (
                    <button
                      className="raw-log-search-clear"
                      onClick={() => setRawLogSearchQuery('')}
                      title="Clear search"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="raw-log-viewer-content">
              {loadingRawLog ? (
                <div className="raw-log-loading">Loading...</div>
              ) : rawLogData ? (
                <pre className="raw-log-json">
                  {(() => {
                    const jsonString = JSON.stringify(rawLogData, null, 2);
                    if (!rawLogSearchQuery || rawLogSearchQuery.trim() === '') {
                      return jsonString;
                    }
                    
                    // Highlight search matches in JSON
                    const searchTerm = rawLogSearchQuery.trim();
                    const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const regex = new RegExp(`(${escapedTerm})`, 'gi');
                    const parts = jsonString.split(regex);
                    
                    // When splitting with capturing group, every odd index is a match
                    return parts.map((part, i) => 
                      i % 2 === 1 ? (
                        <mark key={i} className="raw-log-search-highlight">{part}</mark>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    );
                  })()}
                </pre>
              ) : null}
            </div>
            <div className="raw-log-viewer-footer">
              <button onClick={closeRawLogViewer} className="raw-log-viewer-close-btn">Close</button>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(rawLogData, null, 2));
                }}
                className="raw-log-viewer-copy-btn"
                disabled={!rawLogData}
              >
                Copy JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LogsModal;

